import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
    LogOut, Users, Plus, Trash2, Mail, Calendar, Shield, 
    UserPlus, Copy, Check, Crown, User, RefreshCw, Download
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function AdminUsers() {
    const { user, logout } = useAuth();
    const [users, setUsers] = useState([]);
    const [invites, setInvites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showInviteForm, setShowInviteForm] = useState(false);
    const [inviteForm, setInviteForm] = useState({ email: '', name: '', role: 'staff' });
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [copiedCode, setCopiedCode] = useState(null);
    const [exporting, setExporting] = useState(false);

    useEffect(function() {
        loadUsers();
        loadInvites();
    }, []);

    async function loadUsers() {
        try {
            const res = await axios.get(API_URL + '/api/admin/users');
            setUsers(res.data);
        } catch (e) {
            if (e.response?.status === 403) {
                toast.error('Admin access required');
            } else {
                toast.error('Failed to load users');
            }
        } finally {
            setLoading(false);
        }
    }

    async function loadInvites() {
        try {
            const res = await axios.get(API_URL + '/api/admin/invites');
            setInvites(res.data);
        } catch (e) {
            console.error('Failed to load invites');
        }
    }

    async function handleInvite(e) {
        e.preventDefault();
        try {
            const res = await axios.post(API_URL + '/api/admin/users/invite', inviteForm);
            const data = res.data;
            if (data.email_sent) {
                toast.success('Invitation email sent to ' + inviteForm.email);
            } else {
                toast.success('Invitation created! Code: ' + data.invite_code + ' (email not sent - share manually)');
            }
            setShowInviteForm(false);
            setInviteForm({ email: '', name: '', role: 'staff' });
            loadInvites();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to create invitation');
        }
    }

    async function handleDelete() {
        if (!userToDelete) return;
        try {
            await axios.delete(API_URL + '/api/admin/users/' + userToDelete.id);
            toast.success('User deleted');
            loadUsers();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to delete user');
        } finally {
            setDeleteDialogOpen(false);
            setUserToDelete(null);
        }
    }

    async function cancelInvite(inviteId) {
        try {
            await axios.delete(API_URL + '/api/admin/invites/' + inviteId);
            toast.success('Invitation cancelled');
            loadInvites();
        } catch (e) {
            toast.error('Failed to cancel invitation');
        }
    }

    async function resendInvite(inviteId) {
        try {
            const res = await axios.post(API_URL + '/api/admin/invites/' + inviteId + '/resend');
            if (res.data.email_sent) {
                toast.success('Invitation email resent!');
            } else {
                toast.error('Failed to resend email - check server logs');
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to resend invitation');
        }
    }

    async function changeRole(userId, newRole) {
        try {
            await axios.patch(API_URL + '/api/admin/users/' + userId + '/role?role=' + newRole);
            toast.success('Role updated to ' + newRole);
            loadUsers();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to update role');
        }
    }

    function copyCode(code) {
        navigator.clipboard.writeText(code);
        setCopiedCode(code);
        toast.success('Invite code copied!');
        setTimeout(function() { setCopiedCode(null); }, 2000);
    }

    async function exportData() {
        setExporting(true);
        try {
            const response = await axios.get(API_URL + '/api/admin/export-data', {
                responseType: 'blob'
            });
            
            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
            link.setAttribute('download', 'bestwillsandtrusts_backup_' + timestamp + '.json');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            
            toast.success('Data exported successfully!');
        } catch (e) {
            toast.error('Failed to export data');
            console.error(e);
        } finally {
            setExporting(false);
        }
    }

    function formatDate(d) {
        return new Date(d).toLocaleDateString('en-US', {
            month: 'short', day: 'numeric', year: 'numeric'
        });
    }

    const pendingInvites = invites.filter(function(i) { return i.status === 'pending'; });

    return React.createElement('div', { className: 'min-h-screen bg-[#FDFCF8]' },
        // Header
        React.createElement('header', { className: 'sticky top-0 z-50 border-b border-stone-200 bg-white/80 backdrop-blur-sm' },
            React.createElement('div', { className: 'flex items-center justify-between px-6 py-4 max-w-7xl mx-auto' },
                React.createElement(Link, { to: '/dashboard', className: 'flex items-center gap-3' },
                    React.createElement('img', { src: '/lion-logo.png', alt: 'BestWillsAndTrusts', className: 'h-10 w-10 object-contain' }),
                    React.createElement('span', { className: 'font-serif text-xl font-semibold hidden sm:block text-amber-600' }, 'BestWillsAndTrusts')
                ),
                React.createElement('nav', { className: 'hidden md:flex items-center gap-8' },
                    React.createElement(Link, { to: '/dashboard', className: 'text-sm font-medium text-muted-foreground hover:text-foreground' }, 'Dashboard'),
                    React.createElement(Link, { to: '/clients', className: 'text-sm font-medium text-muted-foreground hover:text-foreground' }, 'Clients'),
                    React.createElement(Link, { to: '/documents', className: 'text-sm font-medium text-muted-foreground hover:text-foreground' }, 'Documents'),
                    React.createElement(Link, { to: '/templates', className: 'text-sm font-medium text-muted-foreground hover:text-foreground' }, 'Templates'),
                    React.createElement(Link, { to: '/admin/users', className: 'text-sm font-medium text-primary' }, 'Users')
                ),
                React.createElement('div', { className: 'flex items-center gap-4' },
                    React.createElement('span', { className: 'text-sm text-muted-foreground hidden sm:block' }, user?.name),
                    React.createElement(Button, { variant: 'ghost', size: 'sm', onClick: logout, className: 'rounded-none' },
                        React.createElement(LogOut, { className: 'h-4 w-4 mr-2' }), 'Sign Out'
                    )
                )
            )
        ),

        // Main content
        React.createElement('main', { className: 'max-w-7xl mx-auto px-6 py-8' },
            // Page header
            React.createElement(motion.div, { 
                initial: { opacity: 0, y: 20 }, 
                animate: { opacity: 1, y: 0 },
                className: 'flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8'
            },
                React.createElement('div', null,
                    React.createElement('h1', { className: 'font-serif text-3xl lg:text-4xl mb-2' }, 'User Management'),
                    React.createElement('p', { className: 'text-muted-foreground' }, 'Manage staff accounts and invitations')
                ),
                React.createElement('div', { className: 'flex gap-2' },
                    React.createElement(Button, { 
                        variant: 'outline',
                        onClick: exportData,
                        disabled: exporting,
                        className: 'rounded-none font-medium',
                        'data-testid': 'export-data-btn'
                    },
                        React.createElement(Download, { className: 'h-4 w-4 mr-2' }), 
                        exporting ? 'Exporting...' : 'Export Data'
                    ),
                    React.createElement(Button, { 
                        onClick: function() { setShowInviteForm(true); },
                        className: 'rounded-none font-medium uppercase tracking-wide',
                        'data-testid': 'invite-user-btn'
                    },
                        React.createElement(UserPlus, { className: 'h-4 w-4 mr-2' }), 'Invite User'
                    )
                )
            ),

            // Invite Form Card
            showInviteForm && React.createElement(motion.div, {
                initial: { opacity: 0, y: -10 },
                animate: { opacity: 1, y: 0 },
                className: 'mb-8'
            },
                React.createElement(Card, { className: 'rounded-none border-amber-200 bg-amber-50/30' },
                    React.createElement(CardHeader, null,
                        React.createElement(CardTitle, { className: 'font-serif text-lg' }, 'Invite New User'),
                        React.createElement(CardDescription, null, 'Send an invitation to join the team')
                    ),
                    React.createElement(CardContent, null,
                        React.createElement('form', { onSubmit: handleInvite, className: 'space-y-4' },
                            React.createElement('div', { className: 'grid grid-cols-1 md:grid-cols-3 gap-4' },
                                React.createElement('div', { className: 'space-y-2' },
                                    React.createElement(Label, null, 'Full Name'),
                                    React.createElement(Input, {
                                        value: inviteForm.name,
                                        onChange: function(e) { setInviteForm(Object.assign({}, inviteForm, { name: e.target.value })); },
                                        placeholder: 'Jane Smith',
                                        required: true,
                                        className: 'rounded-none',
                                        'data-testid': 'invite-name-input'
                                    })
                                ),
                                React.createElement('div', { className: 'space-y-2' },
                                    React.createElement(Label, null, 'Email'),
                                    React.createElement(Input, {
                                        type: 'email',
                                        value: inviteForm.email,
                                        onChange: function(e) { setInviteForm(Object.assign({}, inviteForm, { email: e.target.value })); },
                                        placeholder: 'jane@lawfirm.com',
                                        required: true,
                                        className: 'rounded-none',
                                        'data-testid': 'invite-email-input'
                                    })
                                ),
                                React.createElement('div', { className: 'space-y-2' },
                                    React.createElement(Label, null, 'Role'),
                                    React.createElement('select', {
                                        value: inviteForm.role,
                                        onChange: function(e) { setInviteForm(Object.assign({}, inviteForm, { role: e.target.value })); },
                                        className: 'w-full h-10 px-3 border border-input bg-background text-sm rounded-none focus:outline-none focus:ring-2 focus:ring-ring',
                                        'data-testid': 'invite-role-select'
                                    },
                                        React.createElement('option', { value: 'staff' }, 'Staff'),
                                        React.createElement('option', { value: 'attorney' }, 'Attorney'),
                                        React.createElement('option', { value: 'intake_coordinator' }, 'Intake Coordinator'),
                                        React.createElement('option', { value: 'admin' }, 'Admin')
                                    )
                                )
                            ),
                            React.createElement('div', { className: 'flex gap-2' },
                                React.createElement(Button, { type: 'submit', className: 'rounded-none', 'data-testid': 'send-invite-btn' }, 'Send Invitation'),
                                React.createElement(Button, { 
                                    type: 'button', 
                                    variant: 'outline', 
                                    className: 'rounded-none',
                                    onClick: function() { setShowInviteForm(false); }
                                }, 'Cancel')
                            )
                        )
                    )
                )
            ),

            // Pending Invitations
            pendingInvites.length > 0 && React.createElement(motion.div, {
                initial: { opacity: 0 },
                animate: { opacity: 1 },
                className: 'mb-8'
            },
                React.createElement('h2', { className: 'font-serif text-xl mb-4 flex items-center gap-2' },
                    React.createElement(Mail, { className: 'h-5 w-5' }), 'Pending Invitations'
                ),
                React.createElement('div', { className: 'space-y-3' },
                    pendingInvites.map(function(invite) {
                        return React.createElement(Card, { key: invite.id, className: 'rounded-none border-stone-200' },
                            React.createElement(CardContent, { className: 'p-4' },
                                React.createElement('div', { className: 'flex items-center justify-between' },
                                    React.createElement('div', { className: 'flex items-center gap-4' },
                                        React.createElement('div', { className: 'h-10 w-10 bg-amber-100 flex items-center justify-center' },
                                            React.createElement(Mail, { className: 'h-5 w-5 text-amber-600' })
                                        ),
                                        React.createElement('div', null,
                                            React.createElement('p', { className: 'font-medium' }, invite.name),
                                            React.createElement('p', { className: 'text-sm text-muted-foreground' }, invite.email)
                                        ),
                                        React.createElement(Badge, { 
                                            variant: 'outline', 
                                            className: 'rounded-none ml-2 ' + (invite.role === 'admin' ? 'border-amber-500 text-amber-600' : '')
                                        }, invite.role)
                                    ),
                                    React.createElement('div', { className: 'flex items-center gap-3' },
                                        React.createElement('div', { className: 'text-right' },
                                            React.createElement('p', { className: 'text-xs text-muted-foreground' }, 'Invite Code'),
                                            React.createElement('div', { className: 'flex items-center gap-1' },
                                                React.createElement('code', { className: 'font-mono text-sm bg-stone-100 px-2 py-1' }, invite.invite_code),
                                                React.createElement(Button, {
                                                    variant: 'ghost',
                                                    size: 'icon',
                                                    className: 'h-8 w-8',
                                                    onClick: function() { copyCode(invite.invite_code); }
                                                },
                                                    copiedCode === invite.invite_code
                                                        ? React.createElement(Check, { className: 'h-4 w-4 text-green-600' })
                                                        : React.createElement(Copy, { className: 'h-4 w-4' })
                                                )
                                            )
                                        ),
                                        React.createElement(Button, {
                                            variant: 'ghost',
                                            size: 'sm',
                                            onClick: function() { resendInvite(invite.id); },
                                            className: 'text-amber-600 hover:text-amber-700 hover:bg-amber-50',
                                            'data-testid': 'resend-invite-btn-' + invite.id
                                        },
                                            React.createElement(RefreshCw, { className: 'h-4 w-4 mr-1' }),
                                            'Resend'
                                        ),
                                        React.createElement(Button, {
                                            variant: 'ghost',
                                            size: 'icon',
                                            onClick: function() { cancelInvite(invite.id); },
                                            className: 'text-muted-foreground hover:text-destructive'
                                        },
                                            React.createElement(Trash2, { className: 'h-4 w-4' })
                                        )
                                    )
                                )
                            )
                        );
                    })
                )
            ),

            // Users List
            React.createElement('div', null,
                React.createElement('h2', { className: 'font-serif text-xl mb-4 flex items-center gap-2' },
                    React.createElement(Users, { className: 'h-5 w-5' }), 'Team Members (', users.length, ')'
                ),
                loading
                    ? React.createElement('div', { className: 'space-y-3' },
                        [1, 2, 3].map(function(i) {
                            return React.createElement('div', { key: i, className: 'h-20 bg-stone-100 animate-pulse' });
                        })
                    )
                    : React.createElement('div', { className: 'space-y-3' },
                        users.map(function(u) {
                            const isCurrentUser = u.id === user?.id;
                            return React.createElement(Card, { 
                                key: u.id, 
                                className: 'rounded-none border-stone-200 ' + (isCurrentUser ? 'border-amber-300 bg-amber-50/20' : '')
                            },
                                React.createElement(CardContent, { className: 'p-4' },
                                    React.createElement('div', { className: 'flex items-center justify-between' },
                                        React.createElement('div', { className: 'flex items-center gap-4' },
                                            React.createElement('div', { 
                                                className: 'h-10 w-10 flex items-center justify-center ' + 
                                                    (u.role === 'admin' ? 'bg-amber-100' : u.role === 'attorney' ? 'bg-blue-100' : u.role === 'intake_coordinator' ? 'bg-green-100' : 'bg-stone-100')
                                            },
                                                u.role === 'admin'
                                                    ? React.createElement(Crown, { className: 'h-5 w-5 text-amber-600' })
                                                    : React.createElement(User, { className: 'h-5 w-5 text-stone-600' })
                                            ),
                                            React.createElement('div', null,
                                                React.createElement('p', { className: 'font-medium flex items-center gap-2' },
                                                    u.name,
                                                    isCurrentUser && React.createElement('span', { className: 'text-xs text-muted-foreground' }, '(you)')
                                                ),
                                                React.createElement('p', { className: 'text-sm text-muted-foreground flex items-center gap-1' },
                                                    React.createElement(Mail, { className: 'h-3 w-3' }), u.email
                                                )
                                            ),
                                            React.createElement(Badge, {
                                                variant: u.role === 'admin' ? 'default' : 'outline',
                                                className: 'rounded-none ml-2 ' + (u.role === 'admin' ? 'bg-amber-500' : u.role === 'attorney' ? 'border-blue-500 text-blue-600' : u.role === 'intake_coordinator' ? 'border-green-500 text-green-600' : '')
                                            }, u.role === 'intake_coordinator' ? 'Intake Coord.' : u.role)
                                        ),
                                        React.createElement('div', { className: 'flex items-center gap-3' },
                                            React.createElement('span', { className: 'text-sm text-muted-foreground flex items-center gap-1' },
                                                React.createElement(Calendar, { className: 'h-3.5 w-3.5' }),
                                                formatDate(u.created_at)
                                            ),
                                            !isCurrentUser && React.createElement(React.Fragment, null,
                                                React.createElement('select', {
                                                    value: u.role,
                                                    onChange: function(e) { changeRole(u.id, e.target.value); },
                                                    className: 'h-9 px-2 border border-input bg-background text-sm rounded-none focus:outline-none',
                                                    'data-testid': 'change-role-' + u.id
                                                },
                                                    React.createElement('option', { value: 'staff' }, 'Staff'),
                                                    React.createElement('option', { value: 'attorney' }, 'Attorney'),
                                                    React.createElement('option', { value: 'intake_coordinator' }, 'Intake Coord.'),
                                                    React.createElement('option', { value: 'admin' }, 'Admin')
                                                ),
                                                React.createElement(Button, {
                                                    variant: 'ghost',
                                                    size: 'icon',
                                                    onClick: function() { setUserToDelete(u); setDeleteDialogOpen(true); },
                                                    className: 'text-muted-foreground hover:text-destructive',
                                                    'data-testid': 'delete-user-' + u.id
                                                },
                                                    React.createElement(Trash2, { className: 'h-4 w-4' })
                                                )
                                            )
                                        )
                                    )
                                )
                            );
                        })
                    )
            )
        ),

        // Delete confirmation dialog
        React.createElement(AlertDialog, { open: deleteDialogOpen, onOpenChange: setDeleteDialogOpen },
            React.createElement(AlertDialogContent, { className: 'rounded-none' },
                React.createElement(AlertDialogHeader, null,
                    React.createElement(AlertDialogTitle, { className: 'font-serif' }, 'Delete User'),
                    React.createElement(AlertDialogDescription, null,
                        'Are you sure you want to delete ', userToDelete?.name, '? This action cannot be undone.'
                    )
                ),
                React.createElement(AlertDialogFooter, null,
                    React.createElement(AlertDialogCancel, { className: 'rounded-none' }, 'Cancel'),
                    React.createElement(AlertDialogAction, {
                        onClick: handleDelete,
                        className: 'rounded-none bg-destructive hover:bg-destructive/90',
                        'data-testid': 'confirm-delete-user-btn'
                    }, 'Delete')
                )
            )
        )
    );
}

export default AdminUsers;
